<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { useAuthStore } from '@stores';
import { useToast } from 'primevue/usetoast';

const emits = defineEmits(['update:isVisible', 'save']);
const props = defineProps({
    userId: {
        type: String,
    },
    isEditForm: {
        type: Boolean,
    },
});

const apiUrl = import.meta.env.VITE_API_URL;
const { t } = useI18n({ useScope: 'global' });
const authStore = useAuthStore();
const toast = useToast();

const formData = ref({
    username: '',
    password: '',
    fullName: '',
    role_id: null,
    admin_id: null,
});

const isSuperAdmin = computed(() => authStore.user.roles.includes('Superadmin'));
const roles = ref([]);
const selectedRole = ref(null);
const admins = ref([]);
const selectedAdmin = ref(null);
const beforeEdit = ref('');

const hasLowercase = computed(() => /[a-z]/.test(formData.value.password));
const hasUppercase = computed(() => /[A-Z]/.test(formData.value.password));
const hasNumeric = computed(() => /\d/.test(formData.value.password));
const hasSpecialCharacter = computed(() => /[!@#$%^&*(),.?":{}]/.test(formData.value.password));
const isLongEnough = computed(() => formData.value.password.length >= 8);

const resolver = () => {
    const errors = {};

    if (!formData.value.username) {
        errors.username = [{ message: t('userCreate.required') }];
    }
    if (props.isEditForm && !formData.value.password) {
        errors.password = [];
    } else if (!formData.value.password || !isLongEnough.value || !hasLowercase.value || !hasUppercase.value || !hasNumeric.value || !hasSpecialCharacter.value) {
        errors.password = [{ message: t('userCreate.required') }];
    }
    if (!formData.value.fullName) {
        errors.fullName = [{ message: t('userCreate.required') }];
    }
    if (!selectedRole.value) {
        errors.role = [{ message: t('userCreate.required') }];
    }
    if (selectedRole.value?.name == 'Moderator' && !selectedAdmin.value && isSuperAdmin.value) {
        errors.admin = [{ message: t('userCreate.required') }];
    }
    return {
        errors,
    };
};

const getRoles = async () => {
    try {
        const response = await axios.get(`${apiUrl}/v1/registration/user`, {
            params: {
                id: props.isEditForm ? props.userId : null,
            },
            headers: {
                Authorization: `Bearer ${authStore.userToken}`,
                'Content-Type': 'application/json',
            },
        });
        const data = response.data.data;
        roles.value = data.roles;
        admins.value = data?.admins;
        if (props.isEditForm) {
            formData.value.username = data?.username;
            formData.value.fullName = data?.fullName;
            selectedRole.value = data.roles?.find((role) => role.selected);
            selectedAdmin.value = data.admins?.find((admin) => admin.selected);
            beforeEdit.value = JSON.stringify({ username: data?.username, fullName: data?.fullName });
        }
    } catch (error) {
        console.error(error);
    }
};

const saveForm = async ({ valid }) => {
    if (!valid) return;
    if ((props.isEditForm && JSON.stringify({ username: formData.value.username, fullName: formData.value.fullName }) !== beforeEdit.value) || formData.value.password) {
        emits('save', { username: formData.value.username, password: formData.value.password, fullName: formData.value.fullName, role_id: selectedRole.value.id, admin_id: selectedAdmin.value?.id });
        closeDialog();
    } else {
        toast.add({ severity: 'info', summary: t('userCreate.noChanges'), life: 3000 });
    }
};

const closeDialog = () => {
    emits('update:isVisible', false);
};

watch(
    roles,
    (newRoles) => {
        if (newRoles.length > 0 && !selectedRole.value) {
            selectedRole.value = isSuperAdmin.value ? null : newRoles[0];
        }
    },
    { immediate: true }
);

onMounted(() => {
    getRoles();
});
</script>

<template>
    <Dialog modal dismissableMask :header="t('userCreate.create')">
        <Form v-slot="$form" @submit="saveForm" :resolver="resolver" :initialValues="formData" class="form-container">
            <div class="form-item">
                <label for="fullName" class="font-semibold">{{ t('userCreate.fullName') }}</label>
                <InputText name="fullName" id="fullName" v-model.trim="formData.fullName" />
                <Message v-if="$form.fullName?.invalid" severity="error" size="small" variant="simple">{{ $form.fullName.error?.message }}</Message>
            </div>

            <div class="form-items">
                <div class="form-item">
                    <label for="role" class="font-semibold">{{ t('userCreate.selectRole') }}</label>
                    <Select :disabled="!isSuperAdmin || props.isEditForm" v-model="selectedRole" :options="roles" optionLabel="name" checkmark :highlightOnSelect="false" class="w-full" name="role" />
                    <Message v-if="$form.role?.invalid" severity="error" size="small" variant="simple">{{ $form.role.error?.message }}</Message>
                </div>

                <div class="form-item" v-if="selectedRole?.name == 'Moderator' && isSuperAdmin">
                    <label for="role" class="font-semibold">{{ t('userCreate.selectAdmin') }}</label>
                    <Select :disabled="props.isEditForm" v-model="selectedAdmin" :options="admins" optionLabel="name" checkmark :highlightOnSelect="false" class="w-full" name="admin" />
                    <Message v-if="$form.admin?.invalid" severity="error" size="small" variant="simple">{{ $form.admin.error?.message }}</Message>
                </div>
            </div>

            <div class="form-items">
                <div class="form-item">
                    <label for="username" class="font-semibold">{{ t('userCreate.username') }}</label>
                    <InputText name="username" id="username" v-model.trim="formData.username" />
                    <Message v-if="$form.username?.invalid" severity="error" size="small" variant="simple">{{ $form.username.error?.message }}</Message>
                </div>

                <div class="form-item">
                    <label for="password" class="font-semibold">{{ props.isEditForm ? t('userCreate.setPassword') : t('userCreate.password') }}</label>
                    <Password v-model="formData.password" name="password" feedback toggleMask :prompt="false" class="custom-password">
                        <template #footer>
                            <Divider />
                            <ul class="pl-2 ml-2 my-0 leading-normal">
                                <li :class="{ 'text-green-600': hasLowercase, 'text-red-600': !hasLowercase }">{{ t('userCreate.lowercase') }}</li>
                                <li :class="{ 'text-green-600': hasUppercase, 'text-red-600': !hasUppercase }">{{ t('userCreate.uppercase') }}</li>
                                <li :class="{ 'text-green-600': hasNumeric, 'text-red-600': !hasNumeric }">{{ t('userCreate.number') }}</li>
                                <li :class="{ 'text-green-600': isLongEnough, 'text-red-600': !isLongEnough }">{{ t('userCreate.length') }}</li>
                                <li :class="{ 'text-green-600': hasSpecialCharacter, 'text-red-600': !hasSpecialCharacter }">{{ t('userCreate.symbol') }} (!@#$%^&*(),.?":{})</li>
                            </ul>
                        </template>
                    </Password>
                    <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">{{ $form.password.error?.message }}</Message>
                </div>
            </div>

            <div class="save-buttons">
                <Button @click="closeDialog" type="button" :label="t('form.cancel')" severity="secondary"></Button>

                <Button type="submit" :label="props.isEditForm ? t('form.save_changes') : t('global.save')" severity="info"></Button>
            </div>
        </Form>
    </Dialog>
</template>

<style scoped>
.form-container {
    @apply flex gap-4 flex-col;
}
.form-items {
    @apply flex  gap-2;
}
.form-item {
    @apply flex flex-col gap-1 w-full;
}
.save-buttons {
    @apply flex justify-end gap-2 items-center;
}
</style>
